import { FC } from 'react';
import {
  ComponentRendering,
  Field,
  GetStaticComponentProps,
  LayoutServiceData,
  LinkField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { GetStaticPropsContext } from 'next';

import { icon_arrow_right } from '@gn-audio/icons';
import {
  Button,
  JabraIcon,
  mapSharedPropertiesToFields,
  resolveColorProperty,
  SitecoreFieldTypes,
} from '@gn-audio/jss-components';
import { ComponentProps } from '@gn-audio/jss-components/lib/component-props';
import { isColorBright } from '@gn-audio/jss-components/utils';
import { getLocaleWithRegion, getRelatedProductsByGroup, Group } from '@gn-audio/jss-core';

import { FamilyGroup, getFamilyGroups } from 'api/productCatalogueApi';
import { Eyebrow } from 'common/components/Eyebrow';
import { getSharedProperty } from 'common/getters';
import { parseSCLookupValue } from 'common/siteCoreHelpers';
import { ServiceCard, ServiceCardType } from 'components/Product/ServicesSpot/ServiceCard';

type ServicesSpotFields = {
  eyebrowText: Field<string>;
  heading: Field<string>;
  description: Field<string>;
  allServicesLinkLabel: Field<string>;
  allServicesLink: LinkField;
  backgroundColor: Field<string>;
  servicesCards: ServiceCardType[];
};

type MatchedRelatedServices = {
  relatedService: Group;
  serviceCard: ServiceCardType;
};

type ServicesSpotProps = {
  fields: SitecoreFieldTypes;
  matchedRelatedServices: MatchedRelatedServices[];
} & ComponentProps;

const removeDuplicatesByKey = (array: Group[], field: string): Group[] => {
  const seen = new Map<string | number | boolean, Group>();

  for (const item of array) {
    const value = item[field as keyof Group];

    if (value != null && !seen.has(value)) {
      seen.set(value, item);
    }
  }

  return Array.from(seen.values());
};

const matchRelatedServicesWithSitecoreServices = (
  relatedServices: Group[],
  servicesCards: ServiceCardType[]
): MatchedRelatedServices[] => {
  const serviceCardLookup = new Map(
    servicesCards.map((card) => [card.serviceName?.value.toLocaleLowerCase() || '', card])
  );

  return relatedServices
    .map((relatedService) => {
      const serviceName = relatedService.name?.toLocaleLowerCase();
      if (!serviceName) return null;

      const matchedCard = [...serviceCardLookup.entries()].find(([key]) =>
        serviceName.includes(key)
      )?.[1];

      return matchedCard ? { relatedService, serviceCard: matchedCard } : null;
    })
    .filter((entry): entry is MatchedRelatedServices => entry !== null);
};

const ServicesSpot: FC<ServicesSpotProps> = ({ fields, matchedRelatedServices }) => {
  const {
    eyebrowText,
    heading,
    description,
    allServicesLinkLabel,
    allServicesLink,
    backgroundColor,
    servicesCards,
  } = mapSharedPropertiesToFields<ServicesSpotFields>(fields);

  const resolvedBackgroundColor = resolveColorProperty(backgroundColor?.value);
  const isBackgroundBright = isColorBright(resolvedBackgroundColor);
  const textColorClass = isBackgroundBright ? 'text-black' : 'text-white';

  if (matchedRelatedServices.length === 0) return null;

  const ServiceCards = () => (
    <>
      {matchedRelatedServices.map((service) => {
        return (
          <ServiceCard
            key={service.serviceCard.id}
            serviceCard={service.serviceCard}
            relatedService={service.relatedService}
          />
        );
      })}
    </>
  );

  return (
    <div style={{ backgroundColor: resolvedBackgroundColor }}>
      <div className="container">
        <div className={classNames('flex flex-col items-center justify-center', textColorClass)}>
          {!!eyebrowText?.value && <Eyebrow align="center" text={eyebrowText?.value} />}
          {!!heading?.value && (
            <h2 className="py-6 text-center text-4xl lg:text-6xl">{heading?.value}</h2>
          )}
          {!!description?.value && <p className="text-center text-2xl">{description?.value}</p>}
        </div>
        {servicesCards.length > 0 && (
          <div className="flex flex-col gap-6 py-8 lg:flex-row lg:gap-0 lg:px-16 lg:py-16">
            <ServiceCards />
          </div>
        )}
        {!!allServicesLink.value.href && !!allServicesLinkLabel?.value && (
          <div className="flex flex-col items-center justify-center">
            <Button
              className="pt-5"
              size="m"
              variant="primary"
              asLink
              href={allServicesLink.value.href}
              iconPlacement="right"
              icon={<JabraIcon Icon={icon_arrow_right} />}
            >
              {allServicesLinkLabel?.value}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
export const getStaticProps: GetStaticComponentProps = async (
  rendering: ComponentRendering,
  layout: LayoutServiceData,
  context: GetStaticPropsContext
) => {
  try {
    const productFamilyId = getSharedProperty(
      layout.sitecore.route?.fields,
      'Product'
    ) as Field<string>;
    const { fields: dataSource, params = {} } = rendering;

    const servicesData = dataSource as unknown as SitecoreFieldTypes;
    const { servicesCards } = mapSharedPropertiesToFields<ServicesSpotFields>(servicesData);
    const familyLookupCode = parseSCLookupValue(productFamilyId?.value);
    const language = getLocaleWithRegion(context.locale);
    const groups = (await getFamilyGroups(familyLookupCode.id, language)) as FamilyGroup[];

    const services = (await getRelatedProductsByGroup(groups[0].groupId, language)) || [];
    const relatedServices = removeDuplicatesByKey(services, 'pageUrl');

    const matchedRelatedServices = matchRelatedServicesWithSitecoreServices(
      relatedServices,
      servicesCards
    );

    return { matchedRelatedServices, params };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { relatedServices: [], params };
  }
};
export default withDatasourceCheck()<ServicesSpotProps>(ServicesSpot);
